import React from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { FaEllipsisV, FaHeart, FaRegHeart } from 'react-icons/fa';
import { oneStepUp, openCampaignCardAction } from '../../../../shared/functions/Functions';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../store/rootReducer';
import { Campaign, ILinkedCampaigns } from '../../../../model/campaign/Campaign';
import { CampaignStatusEnum } from '../../../../enum/CampaignStatusEnum';
import { useNavigate } from 'react-router-dom';
import { CopyIcon, DeleteIcon, EditIcon, UsersIcon } from '../../../../shared/Icons';

interface ICampaignCardActionButtonsProps {
    campaign?: Campaign | ILinkedCampaigns;
    displayModalAction?: (campaign?: Campaign | ILinkedCampaigns, modalType?: string) => void;
    addToShortcuts?: (id: number) => void;
    isActionVisible?: boolean;
    isTableBody?: boolean;
}

export function CampaignCardActionButtons({
    campaign,
    displayModalAction,
    addToShortcuts,
    isActionVisible = false,
    isTableBody = false,
}: ICampaignCardActionButtonsProps) {
    const userType = useSelector((state: IAllStates) => state.auth.user.userType);
    const isClient = useSelector((state: IAllStates) => state.auth.user.isGeneralClient);
    const isTeamCollaborationEnabled = useSelector(
        (state: IAllStates) => state.auth.user.company?.package?.teamCollaboration
    );
    const { t } = useTranslation();
    const navigate = useNavigate();

    return (
        <div className="d-flex justify-content-end">
            {isActionVisible && (
                <Dropdown className="btn-group position-static" drop="down">
                    <Dropdown.Toggle className="mr-2" variant="light" id="dropdown-basic">
                        <FaEllipsisV className="text-secondary" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item
                            className="shortcut-item"
                            onClick={() => addToShortcuts && addToShortcuts(campaign?.id ?? 0)}
                        >
                            <div className="shortcut-content">
                                {campaign?.isFavorite ? (
                                    <FaHeart className="dropdown-menu-icon is-favorite" height={20} width={20} />
                                ) : (
                                    <FaRegHeart className="dropdown-menu-icon" height={20} width={20} />
                                )}

                                {campaign?.isFavorite
                                    ? t('general.button.remove_from_shortcuts')
                                    : t('general.button.add_to_shortcuts')}
                            </div>
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={() =>
                                oneStepUp(
                                    navigate,
                                    String(campaign?.id ?? 0),
                                    campaign?.isBriefingComplete ?? false,
                                    campaign?.briefingStep ?? '8',
                                    isClient
                                )
                            }
                        >
                            <EditIcon width={20} height={20} className="dropdown-menu-icon" />
                            {t('general.button.edit')}
                        </Dropdown.Item>
                        {userType !== 'brand' && (
                            <Dropdown.Item
                                disabled={!isTeamCollaborationEnabled}
                                onClick={() => {
                                    if (isTeamCollaborationEnabled) {
                                        if (displayModalAction) {
                                            displayModalAction(campaign, 'management');
                                        }
                                    }
                                }}
                            >
                                <UsersIcon width={20} height={20} className="dropdown-menu-icon" />
                                {t('general.button.manage_user')} (PRO)
                            </Dropdown.Item>
                        )}
                        {campaign?.canDuplicate ? (
                            <Dropdown.Item
                                onClick={() => displayModalAction && displayModalAction(campaign, 'duplicate')}
                            >
                                <CopyIcon width={20} height={20} className="dropdown-menu-icon" />

                                {t('general.button.duplicate')}
                            </Dropdown.Item>
                        ) : null}
                        {campaign?.canDelete ? (
                            <Dropdown.Item
                                className="text-danger"
                                onClick={() => displayModalAction && displayModalAction(campaign, 'delete')}
                            >
                                <DeleteIcon width={20} height={20} className="dropdown-menu-icon" />
                                {t('general.button.delete')}
                            </Dropdown.Item>
                        ) : null}
                    </Dropdown.Menu>
                </Dropdown>
            )}
            {campaign?.status === CampaignStatusEnum.DRAFT ? (
                <Button onClick={() => openCampaignCardAction(navigate, campaign, isClient)} size="sm">
                    {t('general.button.edit')}
                </Button>
            ) : (
                <Button
                    onClick={() => openCampaignCardAction(navigate, campaign, isClient)}
                    size="sm"
                    variant={isTableBody ? 'outline-primary' : 'primary'}
                >
                    {t('general.open')}
                </Button>
            )}
        </div>
    );
}
